import PropTypes from 'prop-types';
import Select from '@mui/material/Select';
import { Checkbox, Divider, InputLabel, ListItemText, Box } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { format as formatDateFns } from 'date-fns';
import {
  renderCapitalised,
  renderStatusFilter,
  SELECT_ALL_VALUE,
} from 'utils/functions/filterHelpers';
import { STATUS_ITEMS } from 'constants/requestStatus';

const Controls = ({
  statusFilter,
  setStatusFilter,
  setPage,
  setNameFilter,
  nameFilter,
  eduBudgetPeriod,
  setEduBudgetPeriod,
  periods,
}) => {
  const value = eduBudgetPeriod || eduBudgetPeriod.key === 0 ? eduBudgetPeriod.key : '';

  const renderDate = value => {
    const period = periods.find(({ key }) => key === value);
    if (!period) return '';

    return (
      <>
        <Box component="span" sx={{ fontSize: '0.8rem' }}>
          {formatDateFns(period.start, 'dd.MM.yyyy')}
        </Box>
        <Box component="span" sx={{ margin: '0 5px' }}>
          {' '}
          -{' '}
        </Box>
        <Box component="span" sx={{ fontSize: '0.8rem' }}>
          {formatDateFns(period.end, 'dd.MM.yyyy')}{' '}
        </Box>
      </>
    );
  };

  const handleChange = event => {
    const {
      target: { value },
    } = event;
    const newStatusFilter = typeof value === 'string' ? value.split(',') : value;

    if (!statusFilter.includes(SELECT_ALL_VALUE) && newStatusFilter.includes(SELECT_ALL_VALUE)) {
      setStatusFilter([...STATUS_ITEMS, SELECT_ALL_VALUE]);
    } else if (
      statusFilter.includes(SELECT_ALL_VALUE) &&
      !newStatusFilter.includes(SELECT_ALL_VALUE)
    ) {
      setStatusFilter([]);
    } else if (
      statusFilter.includes(SELECT_ALL_VALUE) &&
      newStatusFilter.includes(SELECT_ALL_VALUE) &&
      statusFilter.length !== newStatusFilter.length
    ) {
      const index = newStatusFilter.indexOf(SELECT_ALL_VALUE);
      if (index > -1) {
        newStatusFilter.splice(index, 1);
      }
      setStatusFilter(newStatusFilter);
    } else {
      setStatusFilter(newStatusFilter);
    }

    setPage(0);
  };

  const handleNameFilterChange = event => {
    setNameFilter(event.target.value);
  };

  const handleEduBudgetPeriodFilterChange = ({ target: { value } }) => {
    let result = periods.find(({ key }) => key === value);

    if (value === '') {
      result = '';
    }

    setEduBudgetPeriod(result);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControl
          variant="standard"
          size="small"
          sx={{
            width: '11.5rem',
            '@media (max-width:480px)': {
              width: '100%',
              marginTop: '0.75rem',
            },
            '&:not(:last-child)': {
              marginRight: '15px',
            },
          }}
        >
          <TextField
            onChange={handleNameFilterChange}
            value={nameFilter}
            id="name-filter"
            label="Name"
            variant="standard"
            size="small"
          />
        </FormControl>

        <FormControl
          variant="standard"
          size="small"
          sx={{
            width: '11.5rem',
            '@media (max-width:480px)': {
              width: '100%',
              marginTop: '0.75rem',
            },
            '&:not(:last-child)': {
              marginRight: '15px',
            },
          }}
        >
          <InputLabel id="period-filter-label">Period</InputLabel>
          <Select
            value={value}
            renderValue={renderDate}
            sx={{ marginTop: '16px' }}
            onChange={handleEduBudgetPeriodFilterChange}
            id="period-filter"
            labelId="period-filter-label"
            label="Period"
          >
            <MenuItem key="clear" value="">
              Clear
            </MenuItem>

            {periods.map(value => (
              <MenuItem key={value.key} value={value.key}>
                {renderDate(value.key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="standard"
          size="small"
          sx={{
            width: '11.5rem',
            '@media (max-width:480px)': {
              width: '100%',
              marginTop: '0.75rem',
            },
            '&:not(:last-child)': {
              marginRight: 15,
            },
          }}
        >
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            sx={{ marginTop: '16px' }}
            value={statusFilter}
            onChange={handleChange}
            id="status-filter"
            multiple
            renderValue={statusFilter => renderStatusFilter(statusFilter, true)}
            labelId="status-filter-label"
            label="Status"
          >
            <MenuItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
              <Checkbox checked={statusFilter.indexOf(SELECT_ALL_VALUE) > -1} color="primary" />
              <ListItemText primary={SELECT_ALL_VALUE} />
            </MenuItem>
            <Divider />
            {STATUS_ITEMS.map(value => (
              <MenuItem key={value} value={value}>
                <Checkbox checked={statusFilter.indexOf(value) > -1} color="primary" />
                <ListItemText primary={renderCapitalised(value)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

Controls.defaultProps = {
  eduBudgetPeriod: '',
};

Controls.propTypes = {
  statusFilter: PropTypes.array.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  setNameFilter: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  nameFilter: PropTypes.string.isRequired,
  setEduBudgetPeriod: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
  eduBudgetPeriod: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default Controls;
